import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import { List } from "react-window";

export default function ExchangesScreen({ apiBase , mode = "profiler", openSheet }) {

    const [tab, setTab] = useState("alerts");
    return (
        <div className="w-full h-full p-4"
             style={{
            "--rusty-orange": "rgb(232, 119, 34)",
            "--bs-btn-bg": "#25408f",
        }}>
            <div className="flex border-b border-gray-200 mb-4">
                <TabButton active={tab === "alerts"} onClick={() => setTab("alerts")}>
                    Exchange Alert Service
                </TabButton>
                <TabButton active={tab === "ownership"} onClick={() => setTab("ownership")}>
                    Exchange Ownership List
                </TabButton>
            </div>

            {tab === "alerts" ? (
                <ExchangeAlertService API_BASE={apiBase} />
            ) : (
                <ExchangeOwnershipList API_BASE={apiBase} />
            )}
        </div>
    );
}

function TabButton({ active, onClick, children }) {
    return (
        <button
            className="px-4 py-2 -mb-px border-b-2"
            onClick={onClick}
            style={
                active
                    ? { borderBottomColor: "var(--rusty-orange)", color: "var(--rusty-orange)", fontWeight: 600 }
                    : { borderBottomColor: "transparent", color: "#4b5563" } // gray-600
            }
        >
            {children}
        </button>
    );
}

const Label = ({ children }) => <label className="block text-sm font-medium mb-1">{children}</label>;
const Input = (props) => <input {...props} className={`border rounded px-3 h-10 w-full ${props.className || ""}`} />;


function ExchangeAlertService({ API_BASE }) {
    const [years, setYears] = useState([]);
    const [selectedYears, setSelectedYears] = useState([]);
    const [allYearsToggle, setAllYearsToggle] = useState(false);

    const [vendors, setVendors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [vendorId, setVendorId] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [keyword, setKeyword] = useState("");
    const [redOnlyFirst, setRedOnlyFirst] = useState(false);

    const [loading, setLoading] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [error, setError] = useState(null);

    const [pricingOpen, setPricingOpen] = useState(false);
    const [pricingHtml, setPricingHtml] = useState("Loading...");
    const [pricingHtml2, setPricingHtml2] = useState("Loading...");

    const didInitialFetch = React.useRef(false);

    const authHeaders = () => {
        const t = localStorage.getItem("token");
        return t ? { Authorization: `Bearer ${t}` } : {};
    };

    useEffect(() => {
        let alive = true;
        const run = async () => {
            try {
                const [ysR, vR, cR, cfgR] = await Promise.all([
                    fetch(`${API_BASE}/exchangealerts/years`, { headers: authHeaders() }),
                    fetch(`${API_BASE}/vendors/exchange`, { headers: authHeaders() }),
                    fetch(`${API_BASE}/exchangealerts/categories`, { headers: authHeaders() }),
                ]);

                if (!ysR.ok) throw new Error("Failed to fetch years");
                if (!vR.ok) throw new Error("Failed to fetch vendors");
                if (!cR.ok) throw new Error("Failed to fetch categories");

                const ys = await ysR.json();
                const vs = await vR.json();
                const cs = await cR.json();
                const cfg = cfgR && cfgR.ok ? await cfgR.json() : {};

                if (!alive) return;

                setYears(ys || []);
                setSelectedYears((ys || []).slice(0, 3));
                setVendors(vs || []);
                setCategories(cs || []);
            } catch (e) {
                if (!alive) return;
                setError((e && e.message) || "Failed to load filters");
            }
        };
        run();
        return () => {
            alive = false;
        };
    }, [API_BASE]);

    // All/None behaviour
    useEffect(() => {
        if (allYearsToggle) {
            setSelectedYears(years);
        } else if (selectedYears.length === years.length && years.length > 0) {
            setSelectedYears([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allYearsToggle]);

    const onFetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const url = new URL(`${API_BASE}/exchangealerts`, window.location.origin);
            if (vendorId) url.searchParams.set("vendorId", String(Number(vendorId)));
            if (categoryId) url.searchParams.set("categoryId", String(Number(categoryId)));
            if (keyword && keyword.trim() && keyword !== "Keyword Search") url.searchParams.set("keyword", keyword.trim());
            if (redOnlyFirst) url.searchParams.set("redOnlyFirst", "true");
            (selectedYears || []).forEach((y) => url.searchParams.append("years", String(y)));

            const r = await fetch(url.toString().replace(window.location.origin, ""), { headers: authHeaders() });
            if (!r.ok) throw new Error("Failed to fetch alerts");
            const data = await r.json();
            setAlerts(Array.isArray(data) ? data : []);
        } catch (e) {
            setError((e && e.message) || "Failed to load alerts");
        } finally {
            setLoading(false);
        }
    }, [API_BASE, vendorId, categoryId, keyword, redOnlyFirst, selectedYears]);

    useEffect(() => {
        if (didInitialFetch.current) return;
        if (years.length > 0 && selectedYears.length > 0) {
            didInitialFetch.current = true;
            onFetch();
        }
    }, [years, selectedYears, onFetch]);

    // group by year like JSP (desc)
    const groupedByYear = useMemo(() => {
        const map = new Map();
        for (const a of alerts) {
            const y = a && a.date ? new Date(a.date).getFullYear() : 0;
            if (!map.has(y)) map.set(y, []);
            map.get(y).push(a);
        }
        return Array.from(map.entries()).sort((a, b) => b[0] - a[0]);
    }, [alerts]);

    // legacy behaviours
    const openExchangeModule = (vendorId, sectionId, elementId) => {
        const evt = new CustomEvent("open-exchange-module", { detail: { vendorId, sectionId, elementId } });
        window.dispatchEvent(evt);
    };

    const openPricingModal = async (exchangeAlertId) => {
        setPricingOpen(true);
        setPricingHtml("Loading...");
        setPricingHtml2("Loading...");
        try {
            const r = await fetch(`${API_BASE}/exchangealerts/${exchangeAlertId}/pricing`, { headers: authHeaders() });
            if (!r.ok) throw new Error("Failed to load pricing");
            const payload = await r.json();
            const descriptionHtml = payload && payload.descriptionHtml;
            const pricingsHtml = payload && payload.pricingsHtml;
            setPricingHtml(descriptionHtml || "No description.");
            setPricingHtml2(pricingsHtml || "No pricing found.");
        } catch (e) {
            setPricingHtml("Failed to load.");
            setPricingHtml2("");
        }
    };

    const formatDate = (d) => {
        try {
            const dt = new Date(d);
            return dt.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        } catch {
            return d;
        }
    };

    return (
        <div className="space-y-4" style={{ ["--rusty-orange"]: "#E87722" }}>

            {/* Filters row */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-3">
                    <Label>Keyword</Label>
                    <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Keyword Search" />
                </div>

                <div className="md:col-span-3">
                    <Label>Exchanges</Label>
                    <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} className="border rounded h-10 w-full">
                        <option value="">All Exchanges</option>
                        {vendors.map((v) => (
                            <option key={v.id} value={v.id}>
                                {v.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-3">
                    <Label>Exchange Alerts Category</Label>
                    <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="border rounded h-10 w-full">
                        <option value="0">All Exchange Alerts</option>
                        {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.item}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="md:col-span-3 flex gap-2">
                    <PrimaryButton onClick={onFetch} disabled={loading}>
                        {loading ? "Loading…" : "Search"}
                    </PrimaryButton>
                    <ResetButton
                        onClick={() => {
                            setKeyword("");
                            setVendorId("");
                            setCategoryId("");
                            setSelectedYears([]);
                            setAllYearsToggle(false);
                            setAlerts([]);
                            onFetch();
                        }}
                    >
                        Clear
                    </ResetButton>
                </div>
            </div>

            {/* Years section */}
            <div className="border rounded p-3">
                <div className="flex items-center gap-4 mb-2">
          <span className="font-semibold text-lg" style={{ color: "var(--rusty-orange)" }}>
            Years:
          </span>
                    <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={allYearsToggle} onChange={(e) => setAllYearsToggle(e.target.checked)} />
                        <b>All/None</b>
                    </label>
                </div>
                <div className="flex flex-wrap gap-4">
                    {years.map((y) => {
                        const checked = selectedYears.includes(y);
                        return (
                            <label key={y} className="font-semibold">
                                <input
                                    type="checkbox"
                                    className="mr-1"
                                    checked={checked}
                                    onChange={() => {
                                        setAllYearsToggle(false);
                                        setSelectedYears((prev) => (prev.includes(y) ? prev.filter((p) => p !== y) : [...prev, y]));
                                    }}
                                />
                                {y}
                            </label>
                        );
                    })}
                </div>
            </div>

            {error && <div className="text-red-600">{error}</div>}

            <div className="border rounded p-3 max-h-[575px] overflow-auto">
                {alerts.length === 0 && !loading && <div className="text-gray-500">No exchange alerts have been found</div>}

                {groupedByYear.map(([year, list]) => (
                    <div key={year} className={`mb-4 eaYear eaYear-${year}`}>
                        <h3 className="text-lg font-semibold mb-2">{year}</h3>
                        <ul className="space-y-1">
                            {list.map((a) => {
                                const isPolicy = !!a.isPolicyChange;
                                const isPricing = !!a.isPolicyTypePricing;
                                const canOpenExchangeModule = !!(a.exchangeModule && a.exchangeModule.vendorId && a.exchangeModule.sectionId && a.exchangeModule.elementId);
                                const isLink = !isPolicy && !!a.link;

                                const colorClass =
                                    a.colour === "red" ? "text-red-600" : a.colour === "blue" ? "text-blue-600" : "text-black";

                                const content = isPolicy
                                    ? canOpenExchangeModule
                                        ? (
                                            <a
                                                style={{ cursor: "pointer" }}
                                                className={`${colorClass} underline`}
                                                onClick={() =>
                                                    openExchangeModule(a.exchangeModule.vendorId, a.exchangeModule.sectionId, a.exchangeModule.elementId)
                                                }
                                            >
                                                {a.description}
                                            </a>
                                        )
                                        : isPricing
                                            ? (
                                                <a
                                                    style={{ cursor: "pointer" }}
                                                    className={`${colorClass} underline`}
                                                    onClick={() => openPricingModal(a.id)}
                                                >
                                                    {a.description}
                                                </a>
                                            )
                                            : <span className={colorClass}>{a.description}</span>
                                    : isLink
                                        ? (
                                            <a href={a.link} target="_blank" rel="noreferrer" className={`${colorClass} underline`}>
                                                {a.description}
                                            </a>
                                        )
                                        : <span className={colorClass}>{a.description}</span>;

                                return (
                                    <li key={a.id} className="yearList max-w-[475px] truncate">
                                        {content}
                                        {!isPolicy && <span className="ml-2 text-sm text-gray-500">({formatDate(a.date)})</span>}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
            </div>

            {/* Pricing Modal */}
            {pricingOpen && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setPricingOpen(false)}>
                    <div className="bg-white w-[90vw] max-w-5xl rounded-2xl shadow p-0" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b px-4 py-3">
                            <h5 className="font-semibold">Exchange Alert & Pricings</h5>
                            <button className="text-xl" onClick={() => setPricingOpen(false)}>
                                &times;
                            </button>
                        </div>
                        <div className="p-4 space-y-4 max-h-[70vh] overflow-auto">
                            <div className="ea-description" dangerouslySetInnerHTML={{ __html: pricingHtml }} />
                            <div className="pricings" dangerouslySetInnerHTML={{ __html: pricingHtml2 }} />
                        </div>
                        <div className="border-t px-4 py-3 flex justify-end">
                            <button className="px-3 h-10 rounded border" onClick={() => setPricingOpen(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------- Tab 2: Exchange Ownership List ---------------- */

function ExchangeOwnershipList({ API_BASE }) {
    const [keyword, setKeyword] = useState("");
    const [loading, setLoading] = useState(false);
    const [exchanges, setExchanges] = useState([]);
    const [error, setError] = useState(null);

    const [letterFilter, setLetterFilter] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);

    const activePillStyle = { backgroundColor: "var(--rusty-orange)", color: "#fff", borderColor: "var(--rusty-orange)" };

    const isBlank = (v) => v == null || String(v).trim() === "";
    const shouldInclude = (ex) =>
        !(isBlank(ex.mic) && isBlank(ex.institutiondescription) && isBlank(ex.acr));

    const letters = useMemo(
        () => Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)),
        []
    );

    const authHeaders = () => {
        const t = localStorage.getItem("token");
        return t ? { Authorization: `Bearer ${t}` } : {};
    };

    // helper for field-name variants (camelCase vs. db-style)
    const pick = (obj, keys, fallback = "") =>
        keys.reduce((v, k) => (v ?? obj?.[k]), undefined) ?? fallback;

    const labelOf = (ex) =>
        pick(
            ex,
            ["institutionDescription", "institutiondescription", "reutersExchangeName", "reutersexchangename"],
            `#${ex.id}`
        ).trim();

    // auto-load on first mount
    useEffect(() => {
        onSearch(); // empty keyword => full list
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSearch = async (e, { kw } = {}) => {
        e?.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const url = new URL(`${API_BASE}/api/exchanges`, window.location.origin);
            const effectiveKw = (kw ?? keyword)?.trim();
            if (effectiveKw) url.searchParams.set("keyword", effectiveKw);
            const r = await fetch(url.toString().replace(window.location.origin, ""), {
                headers: { ...authHeaders() },
            });
            if (!r.ok) throw new Error("Failed to load exchanges");
            const data = await r.json();
            setExchanges((Array.isArray(data) ? data : []).filter(shouldInclude));
        } catch (e) {
            setError(e?.message ?? "Failed to load exchanges");
        } finally {
            setLoading(false);
        }
    };

    // group A→Z
    const grouped = useMemo(() => {
        const map = new Map();
        for (const ex of exchanges) {
            const name = labelOf(ex);
            const first = (name.charAt(0) || "#").toUpperCase();
            const key = /^[A-Z]$/.test(first) ? first : "#";
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(ex);
        }
        const entries = Array.from(map.entries()).map(([k, list]) => [
            k,
            list.sort((a, b) => labelOf(a).localeCompare(labelOf(b))),
        ]);
        return entries.sort(([a], [b]) => a.localeCompare(b));
    }, [exchanges]);

    // apply letter filter
    const visibleGroups = useMemo(() => {
        if (!letterFilter) return grouped;
        return grouped.filter(([L]) => L === letterFilter);
    }, [grouped, letterFilter]);

    // open modal using the already-fetched row object
    const openExchange = (ex) => {
        setModalData(ex);
        setShowModal(true);
    };

    return (
        <div className="space-y-4">
            {/* Search */}
            <form onSubmit={onSearch} className="flex flex-col md:flex-row md:items-end gap-3">
                <div className="flex-1">
                    <Label>Exchange</Label>
                    <Input
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        placeholder="Search exchanges…"
                    />
                </div>
                <PrimaryButton type="submit" disabled={loading}>
                    {loading ? "Searching…" : "Search"}
                </PrimaryButton>
                {(keyword || letterFilter) ? (
                    <ResetButton
                        type="button"
                        onClick={() => {
                            setKeyword("");
                            setLetterFilter(null);
                            onSearch(null, { kw: "" });
                        }}
                    >
                        Reset
                    </ResetButton>
                ) : null}
            </form>

            {/* A–Z filter */}
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <button
                    type="button"
                    aria-pressed={!letterFilter}
                    className="px-2 py-1 rounded border hover:bg-gray-50"
                    style={!letterFilter ? activePillStyle : undefined}
                    onClick={() => setLetterFilter(null)}
                    title="Show all"
                >
                    All
                </button>

                {letters.map((L) => {
                    const isActive = letterFilter === L;
                    return (
                        <button
                            key={L}
                            type="button"
                            aria-pressed={isActive}
                            className="px-2 py-1 rounded border hover:bg-gray-50"
                            style={isActive ? activePillStyle : undefined}
                            onClick={() => setLetterFilter((cur) => (cur === L ? null : L))}
                        >
                            <b>{L}</b>
                        </button>
                    );
                })}
            </div>

            {error && <div className="text-red-600">{error}</div>}

            {/* Results */}
            <div className="border rounded p-3 max-h-[575px] overflow-auto">
                {visibleGroups.length === 0 && !loading && (
                    <div className="text-gray-500">No exchanges found</div>
                )}
                <table className="min-w-full">
                    <tbody>
                    {visibleGroups.map(([letter, list]) => (
                        <React.Fragment key={letter}>
                            <tr>
                                <td colSpan={3} className="pt-4">
                                    <a id={`ref-${letter}`} />
                                    <div
                                        className="text-lg font-semibold mb-1"
                                        style={{ color: "var(--rusty-orange)" }}
                                    >
                                        {letter}
                                    </div>
                                    <div style={{ height: 2, background: "var(--rusty-orange)" }} />
                                </td>
                            </tr>

                            {list.map((ex) => (
                                <tr
                                    key={ex.id}
                                    className="cursor-pointer hover:bg-gray-50"
                                    onClick={() => openExchange(ex)}
                                >
                                    <td className="py-2 pr-6">
                                        <b>{labelOf(ex)}</b>
                                    </td>
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Details Modal (no extra fetch) */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setShowModal(false)} />
                    <div className="relative bg-white rounded-2xl shadow-xl w-[640px] max-w-[95vw] p-5">
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-xl font-semibold">Exchange Details</h2>
                            <button className="px-3 py-1 border rounded" onClick={() => setShowModal(false)}>
                                Close
                            </button>
                        </div>

                        {modalData && (
                            <div>
                                <div className="text-orange-600 font-bold mb-3">
                                    {labelOf(modalData)}
                                </div>
                                <table className="w-full border border-black">
                                    <thead>
                                    <tr className="border-b border-black">
                                        <th className="text-left p-2 border-r border-black w-24">MIC</th>
                                        <th className="text-left p-2 border-r border-black">Institution Description</th>
                                        <th className="text-left p-2 w-20">ACR</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td className="p-2 border-r border-black">
                                            {pick(modalData, ["mic", "micCode", "miccode"], "")}
                                        </td>
                                        <td className="p-2 border-r border-black">
                                            {pick(modalData, ["institutionDescription", "institutiondescription"], "")}
                                        </td>
                                        <td className="p-2">
                                            {pick(modalData, ["acr", "ACR"], "")}
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------- UI Helpers ---------------- */

function PrimaryButton({ children, style, ...rest }) {
    return (
        <button
            {...rest}
            className="px-4 py-2 text-white rounded disabled:opacity-60"
            style={{ backgroundColor: "var(--bs-btn-bg)", ...style }}
        >
            {children}
        </button>
    );
}

function ResetButton({ children = "Reset", style, ...rest }) {
    return (
        <button
            {...rest}
            className="px-4 py-2 rounded text-white"
            style={{ backgroundColor: "var(--rusty-orange)", ...style }}
        >
            {children}
        </button>
    );
}

function formatDate(d) {
    try { return new Date(d).toLocaleDateString(); } catch { return d; }
}
